import { useState } from 'react';
import { Monitor, FileText, BarChart3, Calendar, Shield, Users, ChevronLeft, ChevronRight, LogIn } from 'lucide-react';
import { Button } from '../ui/button';
import { Eyebrow } from './MoanaHero';

const SCREENS = [
  {
    id: 'dashboard',
    label: 'Tableau de bord',
    icon: Monitor,
    title: 'Pilotez votre paie en un coup d\'oeil',
    desc: 'Vue synthetique de vos salaries, periodes de paie, declarations et alertes. Evolution de la masse salariale sur 12 mois.',
    color: '#2D5A5A',
  },
  {
    id: 'bulletins',
    label: 'Bulletins de paie',
    icon: FileText,
    title: 'Bulletins conformes CPS & CST',
    desc: 'Génération automatique des bulletins de paie au format PDF. Calcul complet des cotisations CPS (RUAMM, Retraite A/B, Allocations, AT, FSR, MO) et CST 11 tranches.',
    color: '#E8A020',
  },
  {
    id: 'declarations',
    label: 'Déclarations sociales',
    icon: Shield,
    title: 'DMO + CST 4010 en 1 clic',
    desc: 'Génération automatique des formulaires officiels DMO (CPS) et CST 4010 + Annexe 4010-A (DICP) par superposition sur les formulaires vierges réglementaires.',
    color: '#0F766E',
  },
  {
    id: 'reporting',
    label: 'Reporting',
    icon: BarChart3,
    title: '9 rapports pour votre comptabilité',
    desc: 'Livre de paie, OD salaires avec mapping PCG, état des charges, masse salariale, provisions congés, états individuels, registre du personnel, états annuels. Export PDF et Excel.',
    color: '#6366F1',
  },
  {
    id: 'conges',
    label: 'Espace salarié',
    icon: Calendar,
    title: 'Vos salariés consultent leurs bulletins en ligne',
    desc: 'Chaque salarié accède à son profil, ses bulletins de paie, son solde de congés et peut déposer une demande de congé directement — validée par le gestionnaire.',
    color: '#22C55E',
  },
  {
    id: 'admin',
    label: 'Administration',
    icon: Users,
    title: 'Multi-profils et audit complet',
    desc: '5 niveaux d\'accès (Super Admin, Admin, Gestionnaire, Lecture, Salarié). Journal d\'audit traçant chaque action. Gestion des clients, templates et paramètres.',
    color: '#EF4444',
  },
];

const FEATURES_GRID = [
  { icon: FileText, title: 'Bulletins PDF', desc: 'Conformes CPS/CST, charte personnalisable' },
  { icon: Shield, title: 'Déclarations officielles', desc: 'DMO, CST 4010, Annexe 4010-A' },
  { icon: BarChart3, title: '9 rapports', desc: 'Livre de paie, OD, registre, états annuels' },
  { icon: Calendar, title: 'Gestion absences', desc: '9 types, workflow demande/validation' },
  { icon: Users, title: 'Espace salariés', desc: 'Bulletins, congés, demandes en ligne' },
  { icon: Monitor, title: 'Coffre-fort', desc: 'Archivage sécurisé de tous les documents' },
];

export const MoanaPreview = () => {
  const [active, setActive] = useState(0);
  const screen = SCREENS[active];

  return (
    <section id="decouvrir" className="py-20 bg-[#0F1520]" data-testid="preview-section">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10">
        <div className="text-center mb-12">
          <Eyebrow light>Découvrez le logiciel</Eyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-3">
            Un logiciel de paie <em className="italic text-[#F59E0B]">100 % polynésien</em>
          </h2>
          <p className="text-sm text-white/50 leading-relaxed max-w-[560px] mx-auto">
            Conçu à Papeete, pour la Polynésie française. Francs Pacifique, CPS, CST, base 169h, jours fériés PF — tout est natif.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 flex-wrap justify-center mb-8">
          {SCREENS.map((s, i) => (
            <button
              key={s.id}
              onClick={() => setActive(i)}
              data-testid={`preview-tab-${s.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                active === i
                  ? 'bg-white/10 text-white'
                  : 'text-white/40 hover:text-white/70 hover:bg-white/5'
              }`}
            >
              <s.icon className="w-3.5 h-3.5" />{s.label}
            </button>
          ))}
        </div>

        {/* Active screen description */}
        <div className="bg-[#1a2332] border border-white/10 rounded-2xl overflow-hidden" data-testid="preview-content">
          <div className="p-8 md:p-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: screen.color + '20' }}>
                <screen.icon className="w-5 h-5" style={{ color: screen.color }} />
              </div>
              <div>
                <h3 className="text-white text-xl font-bold">{screen.title}</h3>
              </div>
            </div>
            <p className="text-white/60 text-sm leading-relaxed max-w-[700px] mb-6">{screen.desc}</p>

            {/* Mockup window */}
            <div className="bg-[#0f1520] border border-white/10 rounded-xl overflow-hidden">
              <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-white/10 bg-white/[0.02]">
                <div className="w-2.5 h-2.5 rounded-full bg-red-400/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-yellow-400/60" />
                <div className="w-2.5 h-2.5 rounded-full bg-green-400/60" />
                <span className="text-[10px] text-white/20 ml-3 font-mono">moana-paye / {screen.label.toLowerCase()}</span>
              </div>
              <div className="p-6 md:p-8 min-h-[200px] flex items-center justify-center">
                <div className="text-center">
                  <screen.icon className="w-16 h-16 mx-auto mb-4" style={{ color: screen.color, opacity: 0.3 }} />
                  <p className="text-white/30 text-sm font-medium mb-1">{screen.label}</p>
                  <p className="text-white/15 text-xs">Interface Moana Paye — {screen.title}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation arrows */}
          <div className="flex items-center justify-between px-8 pb-6">
            <button
              onClick={() => setActive(a => (a - 1 + SCREENS.length) % SCREENS.length)}
              className="flex items-center gap-1.5 text-white/40 hover:text-white text-xs transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />Precedent
            </button>
            <div className="flex gap-1.5">
              {SCREENS.map((_, i) => (
                <button key={i} onClick={() => setActive(i)}
                  className={`w-2 h-2 rounded-full transition-all ${active === i ? 'bg-[#F59E0B] w-5' : 'bg-white/20 hover:bg-white/40'}`} />
              ))}
            </div>
            <button
              onClick={() => setActive(a => (a + 1) % SCREENS.length)}
              className="flex items-center gap-1.5 text-white/40 hover:text-white text-xs transition-colors"
            >
              Suivant<ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Features grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-10">
          {FEATURES_GRID.map(f => (
            <div key={f.title} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 hover:border-white/10 transition-colors" data-testid={`feature-${f.title.slice(0,8)}`}>
              <f.icon className="w-5 h-5 text-[#F59E0B] mb-2.5" />
              <h4 className="text-white text-sm font-bold mb-1">{f.title}</h4>
              <p className="text-white/40 text-xs leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-10">
          <Button asChild className="bg-[#F59E0B] hover:bg-[#D97706] text-white font-semibold px-8 py-5">
            <a href="/moana-paye/login" data-testid="preview-cta-login">
              <LogIn className="mr-2 w-4 h-4" />Acceder a Moana Paye
            </a>
          </Button>
          <p className="text-white/25 text-xs mt-3">Deja client ? Connectez-vous a votre espace.</p>
        </div>
      </div>
    </section>
  );
};
